import BoxedChartHeader from "components/charts/modern/abstract/BoxedChartHeader";
import DisplayFormatSelector, { DisplayFormat } from "components/charts/modern/inputs/DisplayFormatSelector";
import TickerSymbolSelector from "components/charts/modern/inputs/TickerSymbolSelector";
import classNames from "components/ui/classNames";
import { isBefore, lightFormat, parseISO } from "date-fns";
import type { ITrailingReturnsFieldsFragment } from "lib/graphql/fragments/__generated__/TrailingReturnsFields.generated";
import useSelfie from "lib/hooks/useSelfie";
import { LockIcon } from "lucide-react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

interface TrailingReturnsChartProps {
  trailingReturns: ITrailingReturnsFieldsFragment[];
}

export default function TrailingReturnsChart({ trailingReturns }: TrailingReturnsChartProps) {
  const intl = useIntl();
  const selfie = useSelfie();
  const [displayFormat, setDisplayFormat] = useState(DisplayFormat.Percent);
  const [selectedTickerSymbol, setSelectedTickerSymbol] = useState(trailingReturns[0].tickerSymbol);
  const tickerSymbols = trailingReturns.map(({ tickerSymbol }) => tickerSymbol);
  if (tickerSymbols.length === 0) {
    return null;
  }
  const selectedTrailingReturns = trailingReturns.find(({ tickerSymbol }) => tickerSymbol === selectedTickerSymbol);
  if (!selectedTrailingReturns) {
    return null;
  }

  const percentageFormatter = new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const valueFormatter = new Intl.NumberFormat(intl.locale, {
    notation: "compact",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return (
    <div className="relative space-y-4 rounded-xl border bg-white p-6 ring-4 ring-slate-100 ring-offset-1 ring-offset-slate-100">
      <div className="flex w-full items-center justify-between">
        <BoxedChartHeader
          tickerSymbol={tickerSymbols[0]}
          paywallDisplayOrigin="Trailing Returns chart"
          isNewStuff={isBefore(new Date(), new Date("2024-03-05"))}
          title={<FormattedMessage defaultMessage="Retorno Histórico" id="tHOpsU" />}
          subtitle={
            <FormattedMessage
              defaultMessage="Visualize o retorno de longo prazo das ações. Dividendos e outros eventos são considerados."
              id="v62n71"
            />
          }
        />
      </div>

      <div>
        <div className="relative flex flex-col items-center justify-between sm:flex-row">
          <div className="flex w-full flex-col items-center space-y-4 md:flex-row md:space-x-4 md:space-y-0">
            {tickerSymbols.length >= 2 && (
              <TickerSymbolSelector
                type="single"
                tickerSymbols={tickerSymbols}
                value={[selectedTickerSymbol]}
                onChange={(tickerSymbols) => setSelectedTickerSymbol(tickerSymbols[0])}
              />
            )}

            <DisplayFormatSelector value={displayFormat} onChange={setDisplayFormat} />
          </div>
        </div>

        <div className="mt-8">
          <div className="grid grid-cols-2 items-center gap-4 md:grid-cols-4 md:gap-8 lg:grid-cols-5 lg:gap-14 xl:grid-cols-7">
            {[
              {
                key: "oneDay",
                returnRate: selectedTrailingReturns.oneDay,
                price: selectedTrailingReturns.prices?.oneDay || null,
                label: intl.formatMessage({ defaultMessage: "1 dia", id: "t3JbPb" }),
                mustBePremium: false,
              },
              {
                key: "oneWeek",
                returnRate: selectedTrailingReturns.oneWeek,
                price: selectedTrailingReturns.prices?.oneWeek || null,
                label: intl.formatMessage({ defaultMessage: "1 semana", id: "Z7mLAK" }),
                mustBePremium: false,
              },
              {
                key: "oneMonth",
                returnRate: selectedTrailingReturns.oneMonth,
                price: selectedTrailingReturns.prices?.oneMonth || null,
                label: intl.formatMessage({ defaultMessage: "1 mês", id: "T0sgAr" }),
                mustBePremium: false,
              },
              {
                key: "oneYear",
                returnRate: selectedTrailingReturns.oneYear,
                price: selectedTrailingReturns.prices?.oneYear || null,
                label: intl.formatMessage({ defaultMessage: "1 ano", id: "Lc4l9T" }),
                mustBePremium: false,
              },
              {
                key: "fiveYears",
                returnRate: selectedTrailingReturns.fiveYears,
                price: selectedTrailingReturns.prices?.fiveYears || null,
                label: intl.formatMessage({ defaultMessage: "5 anos", id: "ZfBB6o" }),
                mustBePremium: false,
              },
              {
                key: "tenYears",
                returnRate: selectedTrailingReturns.tenYears,
                price: selectedTrailingReturns.prices?.tenYears || null,
                label: intl.formatMessage({ defaultMessage: "10 anos", id: "SQE0oT" }),
                mustBePremium: true,
              },
              {
                key: "allTime",
                returnRate: selectedTrailingReturns.allTime,
                price: selectedTrailingReturns.prices?.allTime || null,
                label: intl.formatMessage({ defaultMessage: "Histórico", id: "ZyJcEI" }),
                mustBePremium: true,
              },
            ].map((node) => {
              const isPremium = selfie?.isPremium;
              if (node.mustBePremium && !isPremium) {
                return (
                  <div key={node.key} className="space-y-2">
                    <h1 className={classNames("text-2xl font-bold text-slate-600")}>
                      <LockIcon />
                    </h1>
                    <h3 className="whitespace-nowrap text-base font-medium text-slate-600">{node.label}</h3>
                  </div>
                );
              }
              if (node.returnRate === null) {
                return null;
              }
              const { returnRate } = node;
              const isNegativeReturn = returnRate < 0;
              const isPositiveReturn = returnRate > 0;
              const isNeutralReturn = returnRate >= 0 && returnRate < 0.1;
              const isBigValue = returnRate >= 10000;

              return (
                <div key={node.key} className="rounded-xl bg-slate-50 p-4 md:bg-transparent md:p-0">
                  {displayFormat === DisplayFormat.Percent ? (
                    <h1
                      className={classNames("text-2xl font-bold text-slate-600", {
                        "text-rose-600": isNegativeReturn,
                        "text-emerald-600": isPositiveReturn,
                        "text-slate-600": isNeutralReturn,
                      })}
                    >
                      {isBigValue ? `${valueFormatter.format(returnRate)} %` : percentageFormatter.format(returnRate)}
                    </h1>
                  ) : (
                    <h1
                      className={classNames("text-2xl font-bold text-slate-600", {
                        "text-rose-600": isNegativeReturn,
                        "text-emerald-600": isPositiveReturn,
                        "text-slate-600": isNeutralReturn,
                      })}
                    >
                      {isBigValue
                        ? `${percentageFormatter.format(returnRate)} %`
                        : valueFormatter.format(node.price?.close || 0)}
                    </h1>
                  )}
                  <h3 className="whitespace-nowrap text-sm font-bold tracking-wide text-slate-700">{node.label}</h3>
                  {node.price?.tradeDate && (
                    <h6 className="mt-1 text-xs font-medium tracking-normal text-slate-500">
                      {lightFormat(parseISO(node.price?.tradeDate), "dd/MM/yyyy")}
                    </h6>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
